import {
  Component,
  EventEmitter,
  Inject,
  OnInit,
  Output,
  inject,
  HostListener,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { LayoutComponent } from '../../core/layout/layout.component';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef } from 'ag-grid-community';
import {
  Subject,
  debounceTime,
  distinctUntilChanged,
  forkJoin,
  Observable,
  map,
  take,
} from 'rxjs';
import { BranchService } from '../../features/branch/services/branch.service';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { HttpClient } from '@angular/common/http';
import { MatOptionModule } from '@angular/material/core';
import {
  customEmailValidator,
  noWhitespaceValidator,
  numbersOnlyValidator,
} from '../../shared/services/validations';
import { ConfirmDialog } from '../../shared/components/confirm-dialog/confirm-dialog';
import { Router, RouterModule } from '@angular/router';
import { LeadService } from '../../features/leads/services/lead.service';
import { LeadStatus } from '../../core/constants/leadStatus';
import { EmployeeService } from '../../features/employees/employee.service';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import {
  MatTabChangeEvent,
  MatTabGroup,
  MatTabsModule,
} from '@angular/material/tabs';
import {
  CdkDragDrop,
  CdkDropList,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { PermissionService } from '../../shared/services/permission.service';
import { ModuleIds } from '../../shared/permission-constants/permission-const';
import { MatMenuModule } from '@angular/material/menu';
import { CustomerService } from '../../features/customers/services/customer.service';

@Component({
  selector: 'app-leads',
  standalone: true,
  imports: [
    LayoutComponent,
    AgGridAngular,
    MatButtonModule,
    MatDialogModule,
    RouterModule,
    MatTabGroup,
    MatTabsModule,
    CommonModule,
    DragDropModule,
    FormsModule,
    // PermissionDirective
  ],
  templateUrl: './leads.component.html',
  styleUrls: ['./leads.component.scss'],
})
export class LeadsComponent {
  public viewPortSize: boolean = false;
  public rowData: any[] | null = null;
  // Permission = Permission;
  public columnDefs: ColDef[];
  public defaultColDef: ColDef = {
    filter: 'agTextColumnFilter',
    floatingFilter: true,
  };
  public rowSelection: 'single' | 'multiple' = 'multiple';
  public paginationPageSize = 10;
  public paginationPageSizeSelector: number[] | boolean = [10, 25, 50];
  public themeClass: string = 'ag-theme-quartz';
  public loading: boolean = false;
  public error: any = null;
  search: string = '';
  activeTabIndex = 0;
  activeViewTabIndex = 0;
  leadStatus = LeadStatus;
  newLeadsByStatus: any;
  allLeadsByStatus: any;
  inProgressLeadsByStatus: any;
  negotiationLeadsByStatus: any;
  closedWonLeadsByStatus: any;
  closedLostLeadsByStatus: any;
  leadStatusKeys: string[] = ['All', ...Object.keys(LeadStatus)];
  rawLeadStatusKeys: string[] = Object.keys(LeadStatus);
  @ViewChildren('dropList') dropLists!: QueryList<CdkDropList>;
  page = 0;
  pageSize = 10;
  totalPages = 1;
  pageSizeOptions = [5, 10, 20, 50, 100];
  startDate: any;
  endDate: any;
  currentStatus: any[] | undefined;
  connectedDropLists: CdkDropList[] = [];
  totalPagesByStatus: { [key: string]: number } = {};
  leadStatusData: {
    [status: string]: {
      total: number;
      limit: number;
      skip: number;
      totalPage: number;
    };
  } = {};
  canCreateLeads$ = this.permissionService.hasPermission(
    ModuleIds.Leads,
    'Create'
  );
  canEditLeads$ = this.permissionService.hasPermission(ModuleIds.Leads, 'Edit');
  canDeleteLeads$ = this.permissionService.hasPermission(
    ModuleIds.Leads,
    'Delete'
  );
  canDeleteLeads: boolean = false;
  canEditLeads: boolean = false;

  private searchSubject = new Subject<string>();
  profileName: any;

  constructor(
    private leadService: LeadService,
    private toastr: ToastrService,
    private datePipe: DatePipe,
    private router: Router, // private store: Store,
    private permissionService: PermissionService
  ) {
    const loggedInUser: any = localStorage.getItem('loggedInUser');
    const userData = JSON.parse(loggedInUser);
    this.profileName = userData?.profileData?.profileName;
  }

  ngOnInit() {
    this.loadAllLeadsByStatus();
    this.searchSubject
      .pipe(debounceTime(600), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.search = searchTerm;
        this.filterLeads();
      });
    this.leadStatusKeys.forEach((key) => {
      this.leadStatusData[key] = {
        total: 0,
        limit: this.pageSize,
        skip: 0,
        totalPage: 1,
      };
    });
    this.checkViewportSize();
    this.canEditLeads$.subscribe((val) => {
      this.canEditLeads = val;
    });
    this.canDeleteLeads$.subscribe((val) => {
      this.canDeleteLeads = val;
    });
  }

  ngAfterViewInit() {
    this.connectedDropLists = this.dropLists.toArray();

    // Listen for drop list changes (e.g. if *ngFor changes)
    this.dropLists.changes.subscribe(() => {
      this.connectedDropLists = this.dropLists.toArray();
    });
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.checkViewportSize();
  }

  checkViewportSize() {
    if (window.innerWidth > 1200) {
      this.viewPortSize = true;
    } else {
      this.viewPortSize = false;
    }
    this.setAGColumn();
  }

  setAGColumn() {
    this.columnDefs = [
      {
        field: 'leadName',
        headerName: 'Lead Name',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        field: 'email',
        headerName: 'Email',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        field: 'phone',
        headerName: 'Phone',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        field: 'source',
        headerName: 'Source',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: this.viewPortSize ? 1 : 0,
        valueFormatter: (params: any) => {
          return this.getStatusLabel(params.value);
        },
      },
      {
        field: 'companyName',
        headerName: 'Company Name',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        field: 'expectedValue',
        headerName: 'Expected Value',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        field: 'expectedCloseDate',
        headerName: 'Expected Close Date',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        field: 'probability',
        headerName: 'Probability',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        field: 'assignedTo',
        headerName: 'Assigned To',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        field: 'action',
        headerName: 'Action',
        flex: this.viewPortSize ? 1 : 0,
        cellRenderer: (params: any) => {
          const container = document.createElement('div');
          container.style.display = 'flex';
          container.style.alignItems = 'center';
          container.style.gap = '20px';

          const editIcon = document.createElement('img');
          editIcon.src = 'assets/images/icons/icon-edit.svg';
          editIcon.style.cursor = 'pointer';
          editIcon.setAttribute('data-action', 'edit');

          const deleteIcon = document.createElement('img');
          deleteIcon.src = 'assets/images/icons/icon-delete.svg';
          deleteIcon.style.cursor = 'pointer';
          deleteIcon.setAttribute('data-action', 'delete');
          // container.appendChild(editIcon);
          // if(this.profileName === 'admin'){
          // container.appendChild(deleteIcon);
          // }
          const status = params.data?.status;
          // this.store.select(selectPermissions.hasPermission('31', Permission.Edit)).subscribe((canEdit) => {
          if (
            status !== 'ClosedWon' &&
            status !== 'ClosedLost' &&
            this.canEditLeads
          ) {
            container.appendChild(editIcon);
          }
          // });
          // this.store.select(selectPermissions.hasPermission('31', Permission.Delete)).subscribe((canDelete) => {
          if (this.canDeleteLeads) {
            container.appendChild(deleteIcon);
          }
          // });
          return container;
        },
        floatingFilter: false,
        filter: false,
      },
    ];
  }

  onDeleteLead(leadId: number): void {
    const dialogRef = this.dialog.open(ConfirmDialog);

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.deleteLead(leadId);
      }
    });
  }

  private deleteLead(leadId: number): void {
    this.leadService.removeLead(leadId).subscribe({
      next: () => {
        this.toastr.success('Lead deleted successfully!', 'Success');
        this.loadAllLeadsByStatus();
      },
      error: () => {
        this.toastr.error('Failed to delete lead. Please try again.', 'Error');
      },
    });
  }

  fetchLeadData(
    pageSize: number = 10,
    status?: string[]
  ): Observable<{ leads: any[]; total: number; limit: number; skip: number }> {
    if (pageSize) {
      this.pageSize = pageSize;
    }
    const skip = this.page * this.pageSize;
    const input = {
      search: this.search,
      limit: this.pageSize,
      skip,
    };
    const statusToSend =
      !status || status.includes('All') || status.length === 0
        ? undefined
        : status; // 🔥 CHANGED

    return this.leadService
      .findAllLeadsWithPagination(input, statusToSend)
      .pipe(
        map((leadsResponse) => {
          const total = leadsResponse?.total || 0;
          const leads = (leadsResponse?.leads || []).map((lead: any) => {
            const timestamp = Number(lead.expectedCloseDate);
            let formattedDate = '';
            if (!isNaN(timestamp)) {
              formattedDate =
                this.datePipe.transform(new Date(timestamp), 'dd/MM/yyyy') ||
                '';
            }
            const assignedTo = lead.assignedToData
              ? `${lead.assignedToData.firstName || ''} ${
                  lead.assignedToData.lastName || ''
                }`.trim()
              : '';
            return {
              id: lead.id,
              leadName: lead.leadName,
              email: lead.email,
              phone: lead.phone,
              source: lead.source,
              status: lead.status,
              companyName: lead.companyName,
              expectedValue: lead.expectedValue,
              expectedCloseDate: formattedDate,
              probability: lead.probability,
              assignedTo,
            };
          });
          return { leads, total, limit: pageSize, skip };
        })
      );
  }

  onSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    this.search = input.value;
    this.searchSubject.next(this.search);
  }

  nextPage() {
    const statusKey = this.leadStatusKeys[this.activeTabIndex];
    const total = this.leadStatusData[statusKey]?.totalPage || 1;

    if (this.page + 1 < total) {
      this.page++;
      this.fetchFilteredLeads();
    }
  }

  prevPage() {
    if (this.page > 0) {
      this.page--;
      this.fetchFilteredLeads();
    }
  }

  get totalPage(): number {
    const statusKey = this.leadStatusKeys[this.activeTabIndex];
    return this.leadStatusData[statusKey]
      ? this.leadStatusData[statusKey].totalPage
      : 1;
  }

  onPageSizeChange(event: Event): void {
    const newSize = Number((event.target as HTMLSelectElement).value);
    // console.log('New Page Size:', newSize);
    this.pageSize = newSize; // ✅ Correctly update pageSize
    this.page = 0; // ✅ Reset to first page
    // this.fetchLeadData(this.pageSize, this.currentStatus);
    this.loadAllLeadsByStatus();
  }

  readonly dialog = inject(MatDialog);

  openConfirm() {
    const dialogRef = this.dialog.open(ConfirmDialog);
  }

  getStatusLabel(status: string): string {
    return LeadStatus[status as keyof typeof LeadStatus] || status;
  }

  getLeadsByStatus(status: string) {
    switch (status) {
      case 'All':
        return this.allLeadsByStatus;
      case 'New':
        return this.newLeadsByStatus;
      case 'InProgress':
        return this.inProgressLeadsByStatus;
      case 'Negotiation':
        return this.negotiationLeadsByStatus;
      case 'ClosedWon':
        return this.closedWonLeadsByStatus;
      case 'ClosedLost':
        return this.closedLostLeadsByStatus;
      default:
        return [];
    }
  }
  onCellClicked(params: any, type: string) {
    const leadId = params.data?.id;
    if (type === 'list') {
      if (
        params.column.colId === 'action' &&
        params.event.target.dataset.action
      ) {
        let action = params.event.target.dataset.action;

        if (action === 'edit') {
          this.openEdit(params?.data);
        } else if (action === 'delete') {
          this.onDeleteLead(leadId);
        }
      } else {
        const leadId = params.data?.id;
        const dialogRef = this.dialog.open(LeadsDialog, {
          data: {
            id: leadId,
          },
          width: '90vw',
          height: '90vh',
        });
        dialogRef.afterClosed().subscribe((result) => {
          // if (result === true) {
          this.loadAllLeadsByStatus();
          // }
        });
      }
    } else if (type === 'grid') {
      const leadId = params?.id;
      const dialogRef = this.dialog.open(LeadsDialog, {
        data: {
          id: leadId,
        },
        width: '90vw',
        height: '90vh',
      });
      dialogRef.afterClosed().subscribe((result) => {
        // if (result === true) {
        this.loadAllLeadsByStatus();
        // }
      });
    }
  }

  loadAllLeadsByStatus(status?: string) {
    this.loading = true;

    forkJoin({
      All: this.fetchLeadData(this.pageSize, undefined).pipe(take(1)),
      New: this.fetchLeadData(this.pageSize, ['New']).pipe(take(1)),
      InProgress: this.fetchLeadData(this.pageSize, ['InProgress']).pipe(
        take(1)
      ),
      Negotiation: this.fetchLeadData(this.pageSize, ['Negotiation']).pipe(
        take(1)
      ),
      ClosedWon: this.fetchLeadData(this.pageSize, ['ClosedWon']).pipe(take(1)),
      ClosedLost: this.fetchLeadData(this.pageSize, ['ClosedLost']).pipe(
        take(1)
      ),
    }).subscribe({
      next: (result) => {
        this.allLeadsByStatus = result.All.leads || [];
        this.newLeadsByStatus = result.New.leads || [];
        this.inProgressLeadsByStatus = result.InProgress.leads || [];
        this.negotiationLeadsByStatus = result.Negotiation.leads || [];
        this.closedWonLeadsByStatus = result.ClosedWon.leads || [];
        this.closedLostLeadsByStatus = result.ClosedLost.leads || [];

        // New: Store total pages for each tab
        this.leadStatusKeys.forEach((statusKey) => {
          const res = (result as any)[statusKey];

          this.leadStatusData[statusKey] = {
            total: res.total,
            limit: this.pageSize,
            skip: this.page * this.pageSize,
            totalPage: Math.ceil(res.total / this.pageSize),
          };
        });
        // console.log("totalpages",this.totalPagesByStatus);

        this.filterLeads();
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.toastr.error('Failed to load leads.');
      },
    });
  }
  onTabChange(event: MatTabChangeEvent) {
    this.activeTabIndex = event.index;
    this.page = 0; // reset pagination
    const status = this.leadStatusKeys[this.activeTabIndex];
    if (status === 'All') {
      this.currentStatus = undefined;
    }
    this.currentStatus = [status];
    // console.log("tabchange",this.currentStatus);

    this.fetchFilteredLeads();
    // this.findAllLeadsWithPagination(this.pageSize, this.currentStatus);
  }

  fetchFilteredLeads() {
    const statusKey = this.leadStatusKeys[this.activeTabIndex];
    this.fetchLeadData(this.pageSize, this.currentStatus).subscribe({
      next: (res) => {
        switch (statusKey) {
          case 'All':
            this.allLeadsByStatus = res.leads;
            break;
          case 'New':
            this.newLeadsByStatus = res.leads;
            break;
          case 'InProgress':
            this.inProgressLeadsByStatus = res.leads;
            break;
          case 'Negotiation':
            this.negotiationLeadsByStatus = res.leads;
            break;
          case 'ClosedWon':
            this.closedWonLeadsByStatus = res.leads;
            break;
          case 'ClosedLost':
            this.closedLostLeadsByStatus = res.leads;
            break;
        }

        this.leadStatusData[statusKey] = {
          total: res.total,
          limit: this.pageSize,
          skip: this.page * this.pageSize,
          totalPage: Math.ceil(res.total / this.pageSize),
        };

        this.filterLeads();
      },
      error: () => {
        this.toastr.error('Failed to fetch leads.');
      },
    });
  }

  private filterLeads() {
    let activeLeads: any[] = [];

    // Determine which list to filter depending on active tab
    const selectedKey = this.leadStatusKeys[this.activeTabIndex];

    if (selectedKey === 'All') {
      activeLeads = this.allLeadsByStatus || [];
    } else if (selectedKey === 'New') {
      activeLeads = this.newLeadsByStatus || [];
    } else if (selectedKey === 'InProgress') {
      activeLeads = this.inProgressLeadsByStatus || [];
    } else if (selectedKey === 'Negotiation') {
      activeLeads = this.negotiationLeadsByStatus || [];
    } else if (selectedKey === 'ClosedWon') {
      activeLeads = this.closedWonLeadsByStatus || [];
    } else if (selectedKey === 'ClosedLost') {
      activeLeads = this.closedLostLeadsByStatus || [];
    }

    // console.log('activeLeads', activeLeads);

    if (this.search && this.search.trim() !== '') {
      const lowerSearch = this.search.toLowerCase();

      this.rowData = activeLeads.filter((lead) => {
        // Check if any field contains the search text
        return Object.values(lead).some((val) => {
          return (
            val !== null &&
            val !== undefined &&
            val.toString().toLowerCase().includes(lowerSearch)
          );
        });
      });
    } else {
      this.rowData = activeLeads;
    }
  }

  openEdit(leadData: any) {
    const leadId = leadData?.id;
    if (leadId) {
      this.router.navigate([`/leads/edit/${leadId}`]);
    }
  }

  changeView(index: number) {
    this.activeViewTabIndex = index;
    // this.loadAllLeadsByStatus();
  }

  drop(event: CdkDragDrop<any[]>, newStatus: string) {
    if (event.previousContainer === event.container) {
      // Reorder within the same status list!
      moveItemInArray(
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
      // If you want to persist the new order to backend,
      // update the orderField/sequenceField or send new order here!
      return;
    } else {
      // Move between different status columns
      const lead = event.previousContainer.data[event.previousIndex];

      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );

      // Update the lead's status property to new status
      // lead.status = newStatus; // Optionally update order too

      const updateStatus = {
        status: newStatus,
        id: lead.id,
      };

      this.leadService.updateLead(lead.id, updateStatus).subscribe({
        next: () => {
          this.toastr.success(
            `Lead "${lead.leadName}" moved to ${this.getStatusLabel(
              newStatus
            )}`,
            'Success'
          );
          this.loadAllLeadsByStatus();
        },
        error: () => {
          this.toastr.error('Failed to update lead status');
          // Revert UI changes on failure:
          transferArrayItem(
            event.container.data,
            event.previousContainer.data,
            event.currentIndex,
            event.previousIndex
          );
          // lead.status = event.previousContainer.id; // or old status key
        },
      });
    }
  }

  getOtherStatusKeys(excludeKey: string): string[] {
    return Object.keys(LeadStatus).filter((key) => key !== excludeKey);
  }
}

@Component({
  selector: 'app-detail-lead',
  templateUrl: './leads-dialog.html',
  styleUrls: ['./leads.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    NgxMatSelectSearchModule,
    MatMenuModule,
  ],
})
export class LeadsDialog {
  loading: boolean = false;
  leadId: number;
  userId: number;
  leadDetail: any;
  statusUpdateForm: FormGroup;
  notesForm: FormGroup;
  customerForm: FormGroup;
  notesData: any;
  activityForm: FormGroup;
  activityData: any;
  addingActivity = false;
  addActivityCount = 0;
  employeeData: any;
  leadStatusValue: any;
  searchTerm: string = '';
  assignedToValue: any;
  isInitialLoad: boolean = false;
  leadStatus = LeadStatus;
  leadStatusKeys: (keyof typeof LeadStatus)[] = Object.keys(
    LeadStatus
  ) as (keyof typeof LeadStatus)[];
  employeeSearchControl = new FormControl();
  activityTypes = ['Call', 'Meeting', 'Email'];
  isDropdownOpen = false;
  isCustomerSelection = false;
  public customerData: any[] = [];
  private searchSubject = new Subject<string>();
  canAssigneeLeads$ = this.permissionService.hasPermission(
    ModuleIds.Leads,
    'Assignee'
  );
  canSendLeads$ = this.permissionService.hasPermission(ModuleIds.Leads, 'Send');
  canActivityLeads$ = this.permissionService.hasPermission(
    ModuleIds.Leads,
    'Activity'
  );

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private toastr: ToastrService,
    private dialogRef: MatDialogRef<LeadsDialog>,
    private fb: FormBuilder,
    private employeeService: EmployeeService,
    private leadService: LeadService,
    private permissionService: PermissionService,
    private router: Router,
    private customerService: CustomerService
  ) {}

  statusClassMap: { [key: string]: string } = {
    new: 'status-new',
    inprogress: 'status-in-progress',
    negotiation: 'status-negotiation',
    closedwon: 'status-closed-won',
    closedlost: 'status-closed-lost',
  };

  getStatusClass(status: string): string {
    return this.statusClassMap[status.toLowerCase()];
  }
  ngOnInit() {
    this.statusUpdateForm = this.fb.group({
      status: ['', Validators.required],
      assignedTo: ['', Validators.required],
    });
    this.notesForm = this.fb.group({
      content: ['', Validators.required],
    });
    this.customerForm = this.fb.group({
      customerId: [null, Validators.required],
    });
    this.initActivityForm();
    this.fetchPartners();
    this.searchSubject
      .pipe(debounceTime(600), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.fetchPartners(searchTerm);
      });
    if (this.data) {
      // console.log(this.data);
      this.leadId = this.data?.id;
      this.fetchLeadById(this.leadId);
      this.fetchNotes(this.leadId);
      this.fetchActivity(this.leadId);
    }
    this.fetchEmployee();

    this.employeeSearchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((searchText) => {
        this.fetchEmployee(searchText);
      });

    this.statusUpdateForm.get('status')?.valueChanges.subscribe((newStatus) => {
      if (!this.isInitialLoad && this.statusUpdateForm.get('status')?.valid) {
        // console.log('status changing', newStatus);
        this.updateLeadDetail({ status: newStatus });
      }
    });
    // this.customerForm.get('customerId')?.valueChanges.subscribe((newCustomerId) => {
    //   if (this.customerForm.get('customerId')?.valid) {
    //     // console.log('status changing', newStatus);
    //     this.updateLeadDetail({ customerId: newCustomerId });
    //     this.isCustomerSelection=false;
    //   }
    // });

    this.statusUpdateForm
      .get('assignedTo')
      ?.valueChanges.subscribe((newAssignedTo) => {
        if (
          !this.isInitialLoad &&
          this.statusUpdateForm.get('assignedTo')?.valid
        ) {
          const assignedToInput = Number(newAssignedTo);
          this.updateLeadDetail({ assignedTo: assignedToInput });
        }
      });

    const loggedInUser: string | null = localStorage.getItem('loggedInUser');
    if (loggedInUser) {
      const userObject = JSON.parse(loggedInUser);
      this.userId = userObject?.id;
    }
  }

  initActivityForm() {
    this.activityForm = this.fb.group({
      type: ['', Validators.required],
      details: ['', Validators.required],
      outcome: ['', Validators.required],
    });
  }

  fetchLeadById(id: number) {
    this.leadService.fetchLeadById(id).subscribe({
      next: (data) => {
        // console.log(id);

        this.leadDetail = data;
        this.isInitialLoad = true;
        if (this.leadDetail?.status !== 'New') {
          this.statusUpdateForm.patchValue({
            status: this.leadDetail?.status,
            assignedTo: this.leadDetail?.assignedToData?.id,
          });
        }

        setTimeout(() => {
          this.isInitialLoad = false;
        });
      },
      error: (error) => {
        this.toastr.error(error?.message);
      },
    });
  }

  getStatusLabel(status: string): string {
    return LeadStatus[status as keyof typeof LeadStatus] || status;
  }

  onSubmit() {
    if (this.customerForm.valid) {
      const selectedCustomerId = this.customerForm.value.customerId;
      this.updateLeadDetail({ customerId: selectedCustomerId });
      this.isCustomerSelection = false;
    }
  }

  handleNewCustomer() {
    this.dialogRef.close();
    this.router.navigate([`/customers/customer-new`], {
      queryParams: { leadId: this.leadId },
    });
  }

  createQuotation(){
    this.dialogRef.close();
    this.router.navigate([`/quotation/quotation-new`], {
      queryParams: { leadId: this.leadId },
    });
  }

  private fetchPartners(search: string = '') {
    this.customerService.fetchCustomers(search).subscribe({
      next: (partners: any) => {
        this.customerData = partners;
      },
      error: (error) => console.error(error),
    });
  }

  onSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    const searchTerm = input.value;
    this.searchSubject.next(searchTerm);
  }

  fetchEmployee(search: string = '') {
    this.employeeService.getEmployeeApprovers(search).subscribe({
      next: (data) => {
        this.employeeData = data;
      },
      error: (error) => {
        this.toastr.error(error?.message);
      },
    });
  }
  updateLeadDetail(updatedFields: any) {
    if (!this.leadId) return;

    const updatedFieldsInput = {
      ...updatedFields,
      id: this.leadId,
    };

    this.leadService.updateLead(this.leadId, updatedFieldsInput).subscribe({
      next: () => {
        this.toastr.success('Lead updated successfully');
        this.fetchLeadById(this.leadId);
        // this.dialogRef.close(true);
      },
      error: (error) => {
        this.toastr.error(error?.message || 'Update failed');
      },
    });
  }

  fetchNotes(leadId: number, search: string = '') {
    this.leadService.fetchNotes(leadId, search).subscribe({
      next: (data) => {
        // console.log(data);
        this.notesData = data || [];
      },
      error: (error) => {
        this.toastr.error(error?.message);
      },
    });
  }

  addNotes() {
    if (this.notesForm.invalid) {
      this.notesForm.markAllAsTouched();
      return;
    }

    const notesInput = {
      ...this.notesForm.value,
      leadId: this.leadId,
      createdBy: this.userId,
    };

    this.leadService.createNotes(notesInput).subscribe({
      next: (data) => {
        this.notesForm.reset();
        this.fetchNotes(this.leadId);
      },
      error: (error) => {
        this.toastr.error(error?.message);
      },
    });
  }

  fetchActivity(leadId: number, search: string = '') {
    this.leadService.fetchActivities(leadId, search).subscribe({
      next: (data) => {
        this.activityData = data || [];
      },
      error: (error) => {
        this.toastr.error(error?.message);
      },
    });
  }

  showAddActivityForm() {
    this.addingActivity = true;
    this.addActivityCount++;
    this.initActivityForm();
  }

  addActivity() {
    if (this.activityForm.valid) {
      // Add new activity to the array
      const activityInput = {
        ...this.activityForm.value,
        leadId: this.leadId,
        employeeId: this.userId,
      };
      this.leadService.createActivity(activityInput).subscribe({
        next: (data) => {
          this.activityForm.reset();
          this.fetchActivity(this.leadId);
          this.isDropdownOpen = false;
        },
        error: (error) => {
          this.toastr.error(error?.message);
        },
      });
      this.addingActivity = false;
      // Optionally reset form count, or leave as is for cumulative count
    } else {
      this.activityForm.markAllAsTouched();
    }
  }

  cancelActivity() {
    this.addingActivity = false;
  }

  toggleDropdown(event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
    }
    this.isDropdownOpen = !this.isDropdownOpen;
  }
  handleExistingCustomer(event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
    }
    this.isCustomerSelection = !this.isCustomerSelection;
  }

  onClose() {
    this.dialogRef.close();
  }
}
